import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { useScrollState } from "@/hooks/useScrollState"

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Products", path: "/products" },
  { name: "Projects", path: "/projects" },
  { name: "Industries", path: "/industries" },
  { name: "Blogs", path: "/blogs" },
  { name: "About", path: "/about" },
]

export function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { isScrolled } = useScrollState({ threshold: 20 })
  const prefersReducedMotion = useReducedMotion()
  const isTransparent = location.pathname === "/" && !isScrolled

  const isLinkActive = (path: string) => {
    if (path === "/") return location.pathname === path
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <motion.header
        initial={prefersReducedMotion ? false : { y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isTransparent
            ? "bg-transparent"
            : "bg-white/95 backdrop-blur-md shadow-md border-b border-slate-200/70"
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/images/logo_wide.png"
                alt="Maxitherm Works"
                width={240}
                height={48}
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            <nav
              className={cn(
                "hidden lg:flex items-center gap-1 rounded-xl",
                isTransparent
                  ? "bg-slate-900/18 px-2 py-1 backdrop-blur-sm border border-white/20 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.8)]"
                  : ""
              )}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "px-4 py-2 text-sm font-semibold rounded-lg transition-all",
                    isLinkActive(link.path)
                      ? isTransparent
                        ? "bg-white/35 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]"
                        : "text-maxi-red bg-red-50"
                      : isTransparent
                        ? "text-slate-900/90 hover:bg-white/20 hover:text-slate-900"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white/80"
                  )}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button variant="cta" size="lg" asChild>
                <a href="https://wa.me/919404679370" target="_blank" rel="noopener noreferrer">
                  Get a Quote
                </a>
              </Button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              className={cn(
                "lg:hidden p-2 rounded-lg transition-colors",
                isTransparent ? "text-white" : "text-slate-900"
              )}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        <div
          className={cn(
            "h-1 bg-gradient-to-r from-maxi-red via-maxi-cyan to-maxi-red origin-left transition-transform duration-300",
            isScrolled ? "scale-x-100" : "scale-x-0"
          )}
        />
      </motion.header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-16 z-40 lg:hidden bg-white border-b border-slate-200 shadow-xl"
          >
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={handleMobileLinkClick}
                  className={cn(
                    "px-4 py-3 text-base font-medium rounded-lg transition-all",
                    isLinkActive(link.path)
                      ? "text-maxi-red bg-red-50"
                      : "text-slate-600 hover:bg-slate-50"
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 mt-2 border-t flex flex-col gap-3">
                <Button variant="cta" size="lg" className="w-full" asChild>
                  <a href="https://wa.me/919404679370" target="_blank" rel="noopener noreferrer">
                    Get a Quote on WhatsApp
                  </a>
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
